import { useRef, useState, useCallback } from 'react';
import mqtt from 'mqtt/dist/mqtt';

const INITIAL_ROBOT = {
    FID: null, IP: null,
    GC: 0, SP: 0, SO: 0,
    Cmd: 'S', Turn: 'D', Spd: '0',
    MB: 0, CB: 0,
    TL: null,   // sprayer tank level % (null = not reported yet)
    ERR: 0,     // 1 = robot sent an error frame (ELT + EM) and has not sent a normal frame since
};

// Battery %: accepts 50, "50" or "50%"; clamps to 0-100; keeps the previous value if unparsable.
function toPct(v, prev) {
    if (v === undefined || v === null) return prev;
    const n = parseFloat(v);
    return Number.isFinite(n) ? Math.min(100, Math.max(0, n)) : prev;
}

export function useMQTT() {
    const clientRef = useRef(null);
    const [status, setStatus] = useState('offline');
    const [logs, setLogs] = useState([]);
    const [robotState, setRobotState] = useState(INITIAL_ROBOT);
    const [cameraIP, setCameraIP] = useState(null);
    // time (ms) when the robot answered the handshake with FID + IP = "robot connected"; null while not connected
    const [connectedAt, setConnectedAt] = useState(null);

    const addLog = useCallback((msg, type = 'tx') => {
        const ts = new Date().toLocaleTimeString('en-GB', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' });
        setLogs(prev => [...prev.slice(-150), { id: Date.now() + Math.random(), ts, msg, type }]);
    }, []);

    const connect = useCallback((broker, port, user, pass, topic, teleTopic, fid) => {
        if (clientRef.current) { try { clientRef.current.end(true); } catch (_) { } clientRef.current = null; }
        setStatus('connecting');
        setRobotState(INITIAL_ROBOT);
        setCameraIP(null);
        setConnectedAt(null);
        addLog(`Connecting → wss://${broker}:${port}/mqtt`, 'info');

        try {
            const opts = { clientId: `RobotCtrl_${Math.random().toString(16).slice(2, 8)}`, clean: true, reconnectPeriod: 0, connectTimeout: 10000 };
            if (user && user.trim()) { opts.username = user; opts.password = pass; }

            const client = mqtt.connect(`wss://${broker}:${port}/mqtt`, opts);
            clientRef.current = client;

            client.on('connect', () => {
                setStatus('online');
                addLog('MQTT CONNECTED ✓', 'info');
                // Subscribe to telemetry
                client.subscribe(teleTopic, { qos: 0 }, (err) => {
                    if (!err) addLog(`Subscribed ↓ ${teleTopic}`, 'info');
                });
                // HANDSHAKE: only send FID
                const hs = JSON.stringify({ FID: String(fid) });
                client.publish(topic, hs, { qos: 0 }, () => addLog(`↑ HANDSHAKE: ${hs}`, 'tx'));
            });

            client.on('message', (_topic, message) => {
                const raw = message.toString();
                let d = null;
                try { d = JSON.parse(raw); } catch (_) { }

                // 1) update the UI state FIRST so telemetry / battery show immediately
                if (d && typeof d === 'object') {
                    setRobotState(prev => {
                        const next = {
                            FID: d.FID !== undefined ? d.FID : prev.FID,
                            IP: d.IP !== undefined ? d.IP : prev.IP,
                            GC: d.GC !== undefined ? Number(d.GC) : prev.GC,
                            SP: d.SP !== undefined ? Number(d.SP) : prev.SP,
                            SO: d.SO !== undefined ? Number(d.SO) : prev.SO,
                            Cmd: d.Cmd !== undefined ? d.Cmd : prev.Cmd,
                            Turn: d.Turn !== undefined ? d.Turn : prev.Turn,
                            Spd: d.Spd !== undefined ? String(d.Spd) : prev.Spd,
                            MB: toPct(d.MB, prev.MB),
                            CB: toPct(d.CB, prev.CB),
                            TL: d.TL !== undefined ? toPct(d.TL, prev.TL) : prev.TL,
                            ERR: (d.ELT != null && d.EM != null) ? 1 : (d.Cmd !== undefined ? 0 : prev.ERR),
                        };
                        // nothing changed -> return the same object so React skips a re-render
                        return Object.keys(next).every(k => next[k] === prev[k]) ? prev : next;
                    });
                    if (d.IP) { setCameraIP(d.IP); setConnectedAt(prev => prev || Date.now()); }
                }

                // 2) log AFTER the state update
                addLog(`↓ ${raw}`, 'info');
                if (d && d.IP) addLog(`📷 Camera IP: ${d.IP}`, 'info');
            });

            client.on('error', e => { setStatus('offline'); setConnectedAt(null); addLog(`ERROR: ${e.message}`, 'error'); client.end(); });
            client.on('close', () => { setStatus('offline'); setConnectedAt(null); addLog('CONNECTION CLOSED', 'info'); });
            client.on('reconnect', () => { setStatus('connecting'); addLog('RECONNECTING…', 'info'); });
        } catch (e) {
            setStatus('offline');
            addLog(`CONNECT FAIL: ${e.message}`, 'error');
        }
    }, [addLog]);

    const disconnect = useCallback(() => {
        if (clientRef.current) { try { clientRef.current.end(true); } catch (_) { } clientRef.current = null; }
        setStatus('offline'); setRobotState(INITIAL_ROBOT); setCameraIP(null); setConnectedAt(null);
        addLog('DISCONNECTED', 'info');
    }, [addLog]);

    const publish = useCallback((topic, payload) => {
        const msg = JSON.stringify(payload);
        if (clientRef.current && status === 'online') {
            clientRef.current.publish(topic, msg, { qos: 0 }, err => {
                if (err) addLog(`PUB ERR: ${err.message}`, 'error');
                else addLog(`↑ ${msg}`, 'tx');
            });
        } else {
            addLog(`[OFFLINE] ${msg}`, 'warn');
        }
    }, [status, addLog]);

    const clearLogs = useCallback(() => setLogs([]), []);

    return { status, logs, robotState, cameraIP, connectedAt, connect, disconnect, publish, addLog, clearLogs };
}